import { targetSubsIds1, targetSubsIds2, targetSubsIds3 } from "@data/luckLanding";
import { routePathNameChanged } from "@features/app/model";
import { IFlagsmithContract } from "@features/flagsmith/model";
import { parseQueryParams } from "@features/helpers";
import { IPaymentContract, resetSolidgateResponse } from "@features/payment/ui";
import { SolidgatePlan } from "@features/shared/entities/payment";
import { checkoutCloseClicked, landingBeginClicked, userQueryCollected } from "@features/shared/events";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { routerPush } from "@features/shared/router";
import { createEffect, createEvent, createStore, sample } from "effector";

export const changeEmailValue = createEvent<string>();
const toggleLandingPlans = createEvent();
const setSolidgatePlan = createEvent<SolidgatePlan>();
const setCheckoutPopup = createEvent<boolean>();
const setSelectedPlanIndex = createEvent<number>();

const $filteredSolidgatePlans = createStore<SolidgatePlan[]>([]);
const $currentSolidgatePlan = createStore<SolidgatePlan | null>(null).on(setSolidgatePlan, (_, p) => p);
const signupButtonClicked = createEvent<string>();
const $isCheckoutPopupOpen = createStore(false).on(setCheckoutPopup, (_, p) => p);
const $selectedPlanIndex = createStore(1).on(setSelectedPlanIndex, (_, p) => p);

const $landingToggle = createStore(true).on(toggleLandingPlans, (s) => !s);
const $email = createStore("").on(changeEmailValue, (_, p) => p);

const getEmailFromStorageFx = createEffect(() => {
    if (typeof window !== "undefined") {
        const email = sessionStorage.getItem("email");
        return email ? email : "";
    }
    return "";
});


const initCurrentSolidgatePlanFx = createEffect((plans: SolidgatePlan[]) => {
    if (plans.length == 0) return null;
    return plans[1];
});

const trackLandingBeginClickedFx = createEffect((source: string) => {
    landingBeginClicked(source);
});

const saveQueryParamsFx = createEffect(() => {
    if (typeof window !== "undefined") {
        const query = window.location.search;
        if (query) {
            sessionStorage.setItem("query_params", query);

            const parsed = parseQueryParams(query);
            userQueryCollected(parsed);
        }
    }
});

const trackCheckoutPopupCloseFx = createEffect(() => {
    checkoutCloseClicked("luck_landing");
});

sample({
    clock: signupButtonClicked,
    target: trackLandingBeginClickedFx
});

sample({
    clock: signupButtonClicked,
    fn: () => "/luck/set-email",
    target: routerPush
});

sample({
    clock: getEmailFromStorageFx.doneData,
    target: $email
});

sample({
    clock: $filteredSolidgatePlans,
    filter: () => {
        if (typeof window === "undefined") return false;
        return window.location.pathname === "/luck" || window.location.pathname === "/luck/paywall";
    },
    target: initCurrentSolidgatePlanFx
});

sample({
    clock: initCurrentSolidgatePlanFx.doneData,
    target: $currentSolidgatePlan
});

sample({
    source: $isCheckoutPopupOpen,
    filter: (isOpen) => isOpen === false,
    target: [resetSolidgateResponse, trackCheckoutPopupCloseFx]
});

const contract = new ModelContract(
    {
        $email,
        $filteredSolidgatePlans,
        $landingToggle,
        $currentSolidgatePlan,
        $isCheckoutPopupOpen,
        $selectedPlanIndex
    },
    {
        changeEmailValue,
        toggleLandingPlans,
        setSolidgatePlan,
        signupButtonClicked,
        setCheckoutPopup,
        setSelectedPlanIndex
    },
    {},
    {}
);

export type ILuckLandingContract = typeof contract;

export class LuckLandingModule extends ModuleDescription<ILuckLandingContract> {
    constructor(
        public paymentModule: IPaymentContract,
        public flagsModule: IFlagsmithContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: routePathNameChanged,
            filter: () => {
                if (typeof window === "undefined") return false;
                return window.location.pathname === "/luck";
            },
            target: saveQueryParamsFx
        });

        sample({
            clock: [this.flagsModule.stores.$identificationCompleted, routePathNameChanged],
            filter: () => {
                if (typeof window === "undefined") return false;
                return window.location.pathname === "/luck" || window.location.pathname === "/luck/paywall";
            },
            target: this.paymentModule.events.getSolidgatePlans
        });

        sample({
            clock: [
                this.flagsModule.stores.$identificationCompleted,
                $landingToggle,
                this.paymentModule.stores.$solidgatePlans
            ],
            source: {
                plans: this.paymentModule.stores.$solidgatePlans,
                toggle: $landingToggle
            },
            filter: () => {
                if (typeof window === "undefined") return false;
                return (
                    window.location.pathname === "/luck" || window.location.pathname === "/luck/paywall"
                );
            },
            fn: ({ plans, toggle }) => {
                const targetArray = toggle ? targetSubsIds1 : targetSubsIds2;
                return targetArray
                    .map((id) => plans.find((plan) => plan.id === id))
                    .filter((plan) => plan !== undefined)
                    .sort((a, b) => a.prices[0].product_price - b.prices[0].product_price);
            },
            target: $filteredSolidgatePlans
        });

        sample({
            clock: routePathNameChanged,
            filter: () => {
                if (typeof window === "undefined") return false;
                return window.location.pathname === "/luck/set-email";
            },
            target: getEmailFromStorageFx
        });

        sample({
            clock: $isCheckoutPopupOpen,
            source: { $currentSolidgatePlan, $isCheckoutPopupOpen },
            filter: ({ $isCheckoutPopupOpen }) => $isCheckoutPopupOpen,
            fn: ({ $currentSolidgatePlan }) => ({ planId: $currentSolidgatePlan?.id, currency: "USD" }),
            target: this.paymentModule.effects.getSolidgateFormFx
        });

        sample({
            clock: $currentSolidgatePlan,
            target: this.paymentModule.stores.$currentSolidgatePlan
        });
    }
}
